import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IBase } from '@shared/models/api/base';
import { IUIPallet, PalletisingService } from '@shared/services/palletising.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

interface IFormData {
	active: boolean | null;
	description: string | null;
	height: string | null;
	length: string | null;
	maxHeight: string | null;
	maxLayers: string | null;
	maxWeight: string | null;
	name: string | null;
	oversize: string | null;
	spaceBetween: string | null;
	weight: string | null;
	width: string | null;
	dimension: IBase | null;
	type: IBase | null;
	zipCodeRule: string | null;
}

export interface IDialogData {
	pallet: IUIPallet | null;
	types: IBase[];
	dimensions: IBase[];
	rules: number[];
}

@Component({
	selector: 'app-create-update-pallets',
	templateUrl: './create-update-pallets.html',
	styleUrls: ['./create-update-pallets.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdatePalletsDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	public form: FormGroup;
	public httpError: string;
	public isCreatingPallet: boolean;
	public pallet: IUIPallet;
	public dimensions: IBase[];
	public types: IBase[];
	public headingKey: string;
	public rules: number[];

	public constructor(
		@Inject(MAT_DIALOG_DATA) public data: IDialogData,
		public dialogRef: MatDialogRef<CreateUpdatePalletsDialogComponent>,
		private palletisingService: PalletisingService,
		private fb: FormBuilder,
	) {
		this.httpError = '';
		this.pallet = data.pallet ? { ...data.pallet } : this.defaultPallet;
		this.isCreatingPallet = data.pallet === null;
		this.dimensions = data.dimensions;
		this.rules = data.rules;
		this.types = data.types;
	}

	public ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.headingKey = this.pallet.name ? 'masterdata.palletising.pallets.edit-pallet' : 'masterdata.palletising.pallets.create-pallet';
		this.form = this.fb.group({
			name: [this.pallet.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			description: [this.pallet.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			length: [this.pallet.length, [Validators.required, Validators.min(1), Validators.max(10000)]],
			width: [this.pallet.width, [Validators.required, Validators.min(1), Validators.max(10000)]],
			height: [this.pallet.height, [Validators.required, Validators.min(1), Validators.max(10000)]],
			weight: [this.pallet.weight, [Validators.min(1), Validators.max(10000)]],
			maxHeight: [this.pallet.maxHeight, [Validators.min(1), Validators.max(10000)]],
			maxWeight: [this.pallet.maxWeight, [Validators.min(1), Validators.max(10000)]],
			oversize: [this.pallet.oversize, [Validators.min(0), Validators.max(10000)]],
			spaceBetween: [this.pallet.spaceBetween, [Validators.min(1), Validators.max(10000)]],
			maxLayers: [this.pallet.maxLayers, [Validators.min(1), Validators.max(10)]],
			dimension: [this.pallet.dimension],
			type: [this.pallet.type],
			active: [this.pallet.active],
			zipCodeRule: [this.pallet.zipCodeRule],
		});
		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value: IFormData) => {
			this.pallet.name = value.name ? value.name : '';
			this.pallet.description = value.description ? value.description : '';
			this.pallet.length = value.length !== '' && value.length !== null ? Number.parseInt(value.length) : null;
			this.pallet.width = value.width !== '' && value.length !== null ? Number.parseInt(value.width) : null;
			this.pallet.height = value.height !== '' && value.height !== null ? Number.parseInt(value.height) : null;
			this.pallet.active = value.active !== null ? value.active : null;
			this.pallet.weight = value.weight !== '' && value.weight !== null ? Number.parseInt(value.weight) : null;
			this.pallet.maxHeight = value.maxHeight !== '' && value.maxHeight !== null ? Number.parseInt(value.maxHeight) : null;
			this.pallet.maxWeight = value.maxWeight !== '' && value.maxWeight !== null ? Number.parseInt(value.maxWeight) : null;
			this.pallet.oversize = value.oversize !== '' && value.oversize !== null ? Number.parseInt(value.oversize) : null;
			this.pallet.spaceBetween = value.spaceBetween !== '' && value.spaceBetween !== null ? Number.parseInt(value.spaceBetween) : null;
			this.pallet.maxLayers = value.maxLayers !== '' && value.maxLayers !== null ? Number.parseInt(value.maxLayers) : null;
			this.pallet.dimension = value.dimension ? value.dimension : null;
			this.pallet.type = value.type ? value.type : null;
			this.pallet.zipCodeRule = value.zipCodeRule !== '' && value.zipCodeRule !== null ? Number.parseInt(value.zipCodeRule) : null;
		});
	}

	public get controls() {
		return this.form.controls;
	}

	private get defaultPallet(): IUIPallet {
		return {
			active: true,
			activeLabel: '',
			description: null,
			dimension: null,
			dimensionName: null,
			height: null,
			length: null,
			maxHeight: null,
			maxLayers: null,
			maxWeight: null,
			name: null,
			oversize: null,
			self: null,
			spaceBetween: null,
			type: null,
			typeName: null,
			updatedAt: null,
			updatedBy: null,
			weight: null,
			width: null,
			zipCodeRule: null,
		};
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid) return;

		this.getSaveObservable().subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
			},
		});
	}

	private getSaveObservable() {
		if (this.isCreatingPallet) {
			return this.palletisingService.createPallet(this.pallet);
		}
		return this.palletisingService.updatePallet(this.pallet);
	}
}
